// Owner catalog import (#263, #310) — per-business BankettProfi catalog import over
// /api/owner/tenants/:id/catalog. Import only: an upload REPLACES the business's imported catalog
// (rows missing from the file are deactivated server-side; articles an admin added by hand are
// kept, #2007), so a re-import over a non-empty catalog asks for confirmation. Nothing here ever
// exports to BankettProfi.
import React from "react";
import { Banner, Button, Input } from "@/ds";
import { Icon, eur, fmtDate } from "@/lib";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import type { CatalogArticleList, CatalogImportSummary, TenantCatalogStats } from "@/api";
import { useT } from "../i18n";
import { useLiveInvalidation } from "../live/live-provider";
import { bytesToBase64, catalogFileError } from "./screen-tenant-catalog.logic";
import type { PushToast } from "@/notifications/toast-queue";
import styles from "./screen-catalog.module.css";

const ARTICLE_PAGE_SIZE = 50;

export interface TenantCatalogEditorProps {
  /** #378: rendered inline as a business-detail section and the owner Catalog page. */
  tenantId: string;
  pushToast: PushToast;
  /** The top-level Catalog page starts these subsections at h2; business detail nests them below its h2. */
  headingLevel?: 2 | 3;
  /**
   * The Businesses editor shows the stats, the article table and the import. The Catalog page lists
   * the articles itself (#2007), so there this is the import alone.
   */
  presentation?: "embedded" | "page";
  /** The Catalog page reloads its own article list after an import. */
  onImported?: () => void;
}

// #1536: what this business's catalog showed last time, checked like a fresh answer.
function rememberedStats(tenantId: string): TenantCatalogStats | null {
  if (!tenantId) return null;
  const stats = cachedRead("tenantCatalogStats", tenantId);
  return stats && typeof stats === "object" ? stats : null;
}

function rememberedFirstArticles(tenantId: string): CatalogArticleList | null {
  if (!tenantId) return null;
  const list = cachedRead("tenantCatalogArticles", tenantId, { q: undefined, limit: ARTICLE_PAGE_SIZE, offset: 0 });
  return list && typeof list === "object" && Array.isArray(list.items) ? list : null;
}

export function TenantCatalogEditor({ tenantId, pushToast, headingLevel = 3, presentation = "embedded", onImported }: TenantCatalogEditorProps) {
  const t = useT();
  const isPage = presentation === "page";
  const SectionHeading: "h2" | "h3" = headingLevel === 2 ? "h2" : "h3";
  const [stats, setStats] = React.useState<TenantCatalogStats | null>(() => rememberedStats(tenantId));
  const [error, setError] = React.useState<string | null>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [summary, setSummary] = React.useState<CatalogImportSummary | null>(null);
  const fileInput = React.useRef<HTMLInputElement | null>(null);
  // #382: searchable article listing (read-only).
  const [articles, setArticles] = React.useState<CatalogArticleList | null>(() => rememberedFirstArticles(tenantId));
  const [articlesError, setArticlesError] = React.useState<string | null>(null);
  const [articlesLoading, setArticlesLoading] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [offset, setOffset] = React.useState(0);
  const articlesSeq = React.useRef(0);

  const load = React.useCallback(() => {
    if (!tenantId) return;
    setError(null);
    cockpit.tenantCatalogStats(tenantId).then(
      (s) => setStats(s && typeof s === "object" ? s : null),
      (e: any) => setError(e?.message || t("common.error")),
    );
  }, [tenantId, t]);

  const loadArticles = React.useCallback(
    (q: string, off: number) => {
      if (!tenantId || isPage) return;
      const seq = ++articlesSeq.current;
      setArticlesError(null);
      setArticlesLoading(true);
      cockpit
        .tenantCatalogArticles(tenantId, {
          q: q.trim() || undefined,
          limit: ARTICLE_PAGE_SIZE,
          offset: off,
        })
        .then(
          (list) => {
            if (seq !== articlesSeq.current) return;
            setArticlesLoading(false);
            if (list && typeof list === "object" && Array.isArray(list.items)) {
              setArticles(list);
              setArticlesError(null);
            } else {
              setArticlesError(t("common.error"));
            }
          },
          (e: any) => {
            if (seq !== articlesSeq.current) return;
            setArticlesLoading(false);
            setArticlesError(e?.message || t("common.error"));
          },
        );
    },
    [tenantId, t, isPage],
  );

  React.useEffect(() => {
    // Reset per tenant: stale stats/files must not leak between businesses.
    setStats(rememberedStats(tenantId));
    setFile(null);
    setFileError(null);
    setConfirming(false);
    setSummary(null);
    setArticles(rememberedFirstArticles(tenantId));
    setArticlesError(null);
    setQuery("");
    setOffset(0);
    load();
  }, [load]);

  React.useEffect(() => {
    // Debounce keystrokes; an offset change (pager) needs no delay.
    const handle = window.setTimeout(() => loadArticles(query, offset), query ? 250 : 0);
    return () => window.clearTimeout(handle);
  }, [loadArticles, query, offset]);

  // An import running in another session changes both the stats and the article page (#544). The
  // page position travels through a ref so typing in the search box does not re-run this.
  const page = React.useRef({ query, offset });
  page.current = { query, offset };
  const invalidations = useLiveInvalidation("catalog");
  React.useEffect(() => {
    if (invalidations === 0) return;
    load();
    loadArticles(page.current.query, page.current.offset);
  }, [invalidations, load, loadArticles]);

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setSummary(null);
    setConfirming(false);
    setFile(f);
    setFileError(f ? catalogFileError(f) : null);
  };

  const doImport = async () => {
    if (!tenantId || !file || fileError) return;
    // A non-empty catalog gets replaced — make the owner confirm once.
    if (!confirming && (stats?.active ?? 0) > 0) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const result = await cockpit.importTenantCatalog(tenantId, bytesToBase64(bytes), file.name);
      setSummary(result);
      setFile(null);
      setConfirming(false);
      if (fileInput.current) fileInput.current.value = "";
      pushToast({ tone: "success", title: t("businesses.catalog.imported") });
      load();
      setOffset(0);
      loadArticles(query, 0);
      onImported?.();
    } catch (e: any) {
      setError(e?.message || t("common.error"));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const initialArticlesLoading = articles === null && articlesError === null;
  const articlesRefreshing = articles !== null && articlesLoading;
  const articlesState = articlesError
    ? "error"
    : initialArticlesLoading
      ? "initial-loading"
      : articlesRefreshing
        ? "refreshing"
        : articles && articles.items.length > 0
          ? "populated"
          : "empty";

  return (
      <div className={isPage ? styles.importHost : "stack-sm"} style={isPage ? undefined : { paddingBottom: "var(--space-4)" }} data-testid="catalog-editor" data-presentation={presentation}>
        {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}

        {!isPage && <>
        {!stats && !error && <p className="subtle">{t("common.loading")}</p>}
        {stats && (
          <table className="neo-table catstats">
            <tbody>
              <tr><td>{t("businesses.catalog.active")}</td><td>{stats.active} / {stats.total}</td></tr>
              <tr>
                <td>{t("businesses.catalog.lastImport")}</td>
                <td>
                  {stats.lastImportAt
                    ? <time className="catstats__date" dateTime={stats.lastImportAt}>{fmtDate(stats.lastImportAt)}</time>
                    : t("businesses.catalog.never")}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        <SectionHeading className="catalog-section-title">{t("businesses.catalog.articles")}</SectionHeading>
        <div
          className="catlist"
          data-testid="catalog-articles"
          data-state={articlesState}
          role="region"
          aria-label={t("businesses.catalog.resultsLabel")}
        >
          <div className="catlist__bar">
            <Input
              value={query}
              placeholder={t("businesses.catalog.searchPlaceholder")}
              aria-label={t("businesses.catalog.searchPlaceholder")}
              data-testid="catalog-search"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setQuery(e.currentTarget.value);
                setOffset(0);
              }}
            />
            <span className="catlist__count" data-testid="catalog-count">
              {articles ? t("businesses.catalog.matchCount", { count: articles.total }) : "…"}
            </span>
          </div>
          {(initialArticlesLoading || articlesRefreshing) && (
            <p className="catlist__activity" role="status" aria-live="polite">
              {t(initialArticlesLoading
                ? "businesses.catalog.loadingResults"
                : "businesses.catalog.updatingResults")}
            </p>
          )}
          <div
            className="catlist__results"
            aria-busy={initialArticlesLoading || articlesRefreshing}
          >
            {articlesError && <Banner tone="danger" title={t("common.error")}>{articlesError}</Banner>}
            {!articlesError && !articlesRefreshing && articles && articles.items.length === 0 && (
              <p className="subtle catlist__empty">
                {query.trim()
                  ? t("businesses.catalog.noResults")
                  : t("businesses.catalog.emptyCatalog")}
              </p>
            )}
            {articles && articles.items.length > 0 && (
              <div
                className={`catlist__scroll${articlesRefreshing ? " catlist__scroll--updating" : ""}`}
                data-updating={articlesRefreshing ? "true" : undefined}
              >
                <table className="neo-table catlist__table">
                <colgroup>
                  <col className="catlist__col-id" />
                  <col className="catlist__col-name" />
                  <col className="catlist__col-group" />
                  <col className="catlist__col-price" />
                  <col className="catlist__col-status" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t("businesses.catalog.colBpId")}</th>
                    <th>{t("businesses.catalog.colName")}</th>
                    <th>{t("businesses.catalog.colGroup")}</th>
                    <th className="catlist__price">{t("businesses.catalog.colPrice")}</th>
                    <th>
                      {t("businesses.catalog.availability")}{" "}
                      <span className={styles.characteristicsLabel}>{t("businesses.catalog.characteristics")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {articles.items.map((a) => (
                    <tr key={a.id} className={a.active ? undefined : "catlist__row--inactive"}>
                      <td className="neo-mono catlist__id" data-label={t("businesses.catalog.colBpId")}>{a.bpId}</td>
                      <td className="catlist__name-cell">
                        <span className="catlist__name">{a.nameDe}</span>
                        {a.nameEn && <span className="catlist__name-en">{a.nameEn}</span>}
                      </td>
                      <td className="catlist__group" data-label={t("businesses.catalog.colGroup")}>
                        {a.gruppe ?? "—"}
                        {a.sparte && <span className="catlist__sparte"> · {a.sparte}</span>}
                      </td>
                      <td className="catlist__price" data-label={t("businesses.catalog.priceShort")}>
                        <span className="catlist__price-value">
                          {eur(a.priceNet)}
                          {a.priceNet != null && a.unit ? ` / ${a.unit}` : ""}
                        </span>
                      </td>
                      <td className="catlist__status" data-label={t("businesses.catalog.availability")}>
                        <div className={styles.dimensions}>
                          <span className={styles.availability}>
                            {a.active
                              ? <span className="catlist__flag catlist__flag--active">{t("businesses.catalog.flagActive")}</span>
                              : <span className="catlist__flag catlist__flag--off">{t("businesses.catalog.flagInactive")}</span>}
                          </span>
                          <span className={`catlist__flags ${styles.characteristics}`} role="group" aria-label={t("businesses.catalog.characteristics")}>
                            {a.isPackage && <span className="catlist__flag">{t("businesses.catalog.flagPackage")}</span>}
                            {a.isVegan && <span className="catlist__flag">{t("businesses.catalog.flagVegan")}</span>}
                            {a.isVegetarian && <span className="catlist__flag">{t("businesses.catalog.flagVegetarian")}</span>}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            )}
            {articles && articles.total > articles.items.length && (
              <div className="catlist__pager">
                <Button
                  variant="secondary"
                  disabled={articlesLoading || offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - ARTICLE_PAGE_SIZE))}
                >
                  {t("businesses.catalog.pagePrev")}
                </Button>
                <span className="subtle">
                  {t("businesses.catalog.pageRange", {
                    from: articles.total === 0 ? 0 : articles.offset + 1,
                    to: Math.min(articles.offset + articles.items.length, articles.total),
                    total: articles.total,
                  })}
                </span>
                <Button
                  variant="secondary"
                  disabled={articlesLoading || articles.offset + articles.items.length >= articles.total}
                  onClick={() => setOffset(offset + ARTICLE_PAGE_SIZE)}
                >
                  {t("businesses.catalog.pageNext")}
                </Button>
              </div>
            )}
          </div>
        </div>
        </>}

        {summary && (
          <Banner tone="success" title={t("businesses.catalog.imported")}>
            {t("businesses.catalog.summary", {
              imported: summary.imported,
              packages: summary.packages,
              skipped: summary.skipped,
              deactivated: summary.deactivated,
            })}
            {summary.skipped > 0 && (
              <div>
                {t("businesses.catalog.skipBreakdown", {
                  noPrice: summary.skippedByReason.noPositivePrice,
                  internal: summary.skippedByReason.internalGroup,
                  invalid: summary.skippedByReason.invalidRow,
                })}
              </div>
            )}
          </Banner>
        )}

        <section className={`catalog-import stack-sm${isPage ? ` ${styles.import}` : ""}`} aria-labelledby="catalog-import-title">
          <SectionHeading tabIndex={isPage ? -1 : undefined} id="catalog-import-title" className="catalog-section-title">{t("businesses.catalog.upload")}</SectionHeading>
          <p className="subtle management-copy catalog-import__hint">{t("businesses.catalog.hint")}</p>
          {isPage && stats && (
            <p className="subtle management-copy catalog-import__hint" data-testid="catalog-last-import">
              {t("businesses.catalog.lastImport")}:{" "}
              {stats.lastImportAt
                ? <time className="catstats__date" dateTime={stats.lastImportAt}>{fmtDate(stats.lastImportAt)}</time>
                : t("businesses.catalog.never")}
            </p>
          )}
          <label className="catalog-import__file-label" htmlFor="catalog-file">
            {t("businesses.catalog.fileLabel")}
          </label>
          <input
            id="catalog-file"
            className="catalog-import__file"
            ref={fileInput}
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={pick}
            disabled={busy}
            data-testid="catalog-file"
          />
          {fileError && <Banner tone="danger" title={t("common.error")}>{t(fileError)}</Banner>}
          {confirming && (
            <Banner tone="warning" title={t("businesses.catalog.replaceTitle")}>
              {t("businesses.catalog.replaceWarn", { count: stats?.active ?? 0 })}
            </Banner>
          )}
          <div className="catalog-import__actions management-actions">
            <Button
              variant={confirming ? "destructive" : "primary"}
              loading={busy}
              disabled={busy || !file || !!fileError}
              iconLeft={<Icon name="fileCheck" size={16} />}
              onClick={doImport}
            >
              {confirming ? t("businesses.catalog.replaceConfirm") : t("businesses.catalog.import")}
            </Button>
          </div>
        </section>
      </div>
  );
}
